import React from 'react';
import { Loader } from '@exxonmobil/react-unity';
import ReactEcharts from 'echarts-for-react';
import styled from 'styled-components';
import moment from 'moment';
import { OverallValidation } from '../../common/types/TrendsTypes';
import { ErrorBox } from '../../common/components/ErrorBox';

interface Props {
  data: OverallValidation;
  isLoading: boolean;
  error: string;
  onRefresh: () => void;
}

const DomainChart = ({ data, isLoading, error, onRefresh }: Props) => {
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    legend: {
      left: 'center'
    },
    xAxis: {
      type: 'category',
      splitLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        // Display end of each month on x-axis only
        interval: 0,
        formatter(value: any) {
          const date = moment(value).endOf('month').format('DD MMM');
          if (value === date.toUpperCase()) {
            return date.slice(3).toUpperCase();
          }
          return ' ';
        }
      },
      data: data.dates
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    yAxis: {
      type: 'log',
      minorSplitLine: {
        show: true
      },
      min: 1,
      max: 1000
    },
    series: [
      {
        name: 'Endpoints',
        type: 'line',
        showSymbol: false,
        data: data.endpoints
      },
      {
        name: 'Consumers',
        type: 'line',
        showSymbol: false,
        data: data.consumers
      }
    ],
    graphic: {
      type: 'group',
      right: '2%',
      children: [
        {
          type: 'rect',
          z: 100,
          // top: 'center',
          // left: 'center',
          shape: {
            x: 10,
            y: 7,
            width: 120,
            height: 70
          },
          style: {
            fill: '#fff',
            stroke: '#555',
            lineWidth: 1,
            shadowBlur: 8,
            shadowOffsetX: 3,
            shadowOffsetY: 3,
            shadowColor: 'rgba(0,0,0,0.2)'
          }
        },
        {
          type: 'text',
          z: 100,
          // top: 'center',
          // left: 'center',
          style: {
            fill: '#333',
            text: `
        Current
        `,
            font: '14px EMprintBold'
          }
        },
        {
          type: 'text',
          z: 100,
          // top: 'center',
          // left: 'center',
          style: {
            fill: '#333',
            text: `\n
        Endpoints: ${data.endpoints[data.endpoints.length - 1]}
        Consumers: ${data.consumers[data.consumers.length - 1]}
        Domains: ${data.domains && data.domains.length > 0 ? data.domains[data.domains.length - 1] : '-'}
        `,
            font: '14px EMprintRegular'
          }
        }
      ]
    }
  };
  if (data.domains) {
    option.series.push({
      name: 'Domains',
      type: 'line',
      showSymbol: false,
      data: data.domains
    });
  }

  if (error) {
    if (error === '401' || error === '403') {
      return <EmptyDataContainer>Sessions timeout, please close the page and login again.</EmptyDataContainer>;
    }
    return <ErrorBox onRefresh={onRefresh} isRefreshable />;
  }
  if (isLoading) {
    return (
      <EmptyDataContainer style={{ paddingBottom: '2rem' }}>
        <Loader data-testid="domain-chart-loading" className="em-u-margin loader" />
      </EmptyDataContainer>
    );
  }
  if (!data.dates || data.dates.length === 0) {
    return <EmptyDataContainer>No data available</EmptyDataContainer>;
  }
  return (
    <ReactEcharts opts={{ renderer: 'svg', width: 'auto', height: 300 }} option={option} style={{ height: '300px' }} />
  );
};

const EmptyDataContainer = styled.div`
  text-align: center;
  padding-top: 2rem;
  min-height: 5rem;
`;

export default DomainChart;
